export type MedicalNoteOption = {
  label: string;
  value: number;
};

export const MEDICAL_NOTE_TYPE = {
  NOTE: 2,
  PROCEDURE: 3,
  BEHAVIOUR: 4,
  ALLERGY: 5,
  OBSERVATION: 6,
  DENTAL: 7,
  LABORATORY: 8,
  WEIGHT_MEASUREMENT: 9,
  HEIGHT_MEASUREMENT: 10,
  MEDICATION: 11,
  TEMPERATURE_MEASUREMENT: 12,
  PULSE_MEASUREMENT: 13,
  RESPIRATION_MEASUREMENT: 14,
  PHYSICAL_EXAM: 15,
} as const;

export const medicalNoteTypeOptions: MedicalNoteOption[] = [
  { label: "Note", value: MEDICAL_NOTE_TYPE.NOTE },
  { label: "Procedure", value: MEDICAL_NOTE_TYPE.PROCEDURE },
  { label: "Behaviour", value: MEDICAL_NOTE_TYPE.BEHAVIOUR },
  { label: "Allergy", value: MEDICAL_NOTE_TYPE.ALLERGY },
  { label: "Observation", value: MEDICAL_NOTE_TYPE.OBSERVATION },
  { label: "Dental", value: MEDICAL_NOTE_TYPE.DENTAL },
  { label: "Laboratory", value: MEDICAL_NOTE_TYPE.LABORATORY },
  { label: "Weight Measurement", value: MEDICAL_NOTE_TYPE.WEIGHT_MEASUREMENT },
  { label: "Height Measurement", value: MEDICAL_NOTE_TYPE.HEIGHT_MEASUREMENT },
  { label: "Medication", value: MEDICAL_NOTE_TYPE.MEDICATION },
  {
    label: "Temperature Measurement",
    value: MEDICAL_NOTE_TYPE.TEMPERATURE_MEASUREMENT,
  },
  { label: "Pulse Measurement", value: MEDICAL_NOTE_TYPE.PULSE_MEASUREMENT },
  {
    label: "Respiration Measurement",
    value: MEDICAL_NOTE_TYPE.RESPIRATION_MEASUREMENT,
  },
  { label: "Physical Exam", value: MEDICAL_NOTE_TYPE.PHYSICAL_EXAM },
];

export const weightUnitOptions = ["kg", "g", "lb", "oz"];
export const heightUnitOptions = ["m", "cm", "inches"];
export const temperatureUnitOptions = ["fahrenheit", "celcius"];

export const toSelectOptions = (
  options: MedicalNoteOption[] | string[]
) =>
  options.map((option) =>
    typeof option === "string"
      ? { title: option, value: option }
      : { title: option.label, value: String(option.value) }
  );

export const getMedicalNoteTypeLabel = (value: number | undefined) =>
  medicalNoteTypeOptions.find((option) => option.value === value)?.label ??
  (value != null ? `Type ${value}` : "Unknown");

export const isWeightNote = (type: number) => type === MEDICAL_NOTE_TYPE.WEIGHT_MEASUREMENT;
export const isHeightNote = (type: number) => type === MEDICAL_NOTE_TYPE.HEIGHT_MEASUREMENT;
export const isTemperatureNote = (type: number) =>
  type === MEDICAL_NOTE_TYPE.TEMPERATURE_MEASUREMENT;
export const isPulseNote = (type: number) => type === MEDICAL_NOTE_TYPE.PULSE_MEASUREMENT;
export const isRespirationNote = (type: number) =>
  type === MEDICAL_NOTE_TYPE.RESPIRATION_MEASUREMENT;

export const buildMedicalNoteDescription = (values: {
  medicalNoteType: number;
  noteDescription: string;
  weightMeasurement: string;
  weightUnit: string;
  heightMeasurement: string;
  heightUnit: string;
  temperatureMeasurement: string;
  temperatureUnit: string;
  pulseMeasurement: string;
  respirationMeasurement: string;
}) => {
  if (isWeightNote(values.medicalNoteType)) {
    return `${values.weightMeasurement} ${values.weightUnit}`.trim();
  }
  if (isHeightNote(values.medicalNoteType)) {
    return `${values.heightMeasurement} ${values.heightUnit}`.trim();
  }
  if (isTemperatureNote(values.medicalNoteType)) {
    return `${values.temperatureMeasurement} ${values.temperatureUnit}`.trim();
  }
  if (isPulseNote(values.medicalNoteType)) {
    return `${values.pulseMeasurement} beats per minute`.trim();
  }
  if (isRespirationNote(values.medicalNoteType)) {
    return `${values.respirationMeasurement} breaths per minute`.trim();
  }
  return values.noteDescription.trim();
};

export type MedicalNoteFormSeed = {
  noteDescription: string;
  weightMeasurement: string;
  weightUnit: string;
  heightMeasurement: string;
  heightUnit: string;
  temperatureMeasurement: string;
  temperatureUnit: string;
  pulseMeasurement: string;
  respirationMeasurement: string;
};

export const parseMedicalNoteDescription = (
  medicalNoteType: number,
  noteDescription: string
): MedicalNoteFormSeed => {
  const fallback: MedicalNoteFormSeed = {
    noteDescription,
    weightMeasurement: "",
    weightUnit: "kg",
    heightMeasurement: "",
    heightUnit: "m",
    temperatureMeasurement: "",
    temperatureUnit: "celcius",
    pulseMeasurement: "",
    respirationMeasurement: "",
  };

  if (isWeightNote(medicalNoteType)) {
    const match = noteDescription.match(/^(.+?)\s+(kg|g|lb|oz)$/i);
    if (match) {
      return {
        ...fallback,
        weightMeasurement: match[1],
        weightUnit: match[2],
      };
    }
  }

  if (isHeightNote(medicalNoteType)) {
    const match = noteDescription.match(/^(.+?)\s+(m|cm|inches)$/i);
    if (match) {
      return {
        ...fallback,
        heightMeasurement: match[1],
        heightUnit: match[2],
      };
    }
  }

  if (isTemperatureNote(medicalNoteType)) {
    const match = noteDescription.match(/^(.+?)\s+(fahrenheit|celcius)$/i);
    if (match) {
      return {
        ...fallback,
        temperatureMeasurement: match[1],
        temperatureUnit: match[2],
      };
    }
  }

  if (isPulseNote(medicalNoteType)) {
    const match = noteDescription.match(/^(\d+(?:\.\d+)?)\s+beats per minute$/i);
    if (match) {
      return {
        ...fallback,
        pulseMeasurement: match[1],
      };
    }
  }

  if (isRespirationNote(medicalNoteType)) {
    const match = noteDescription.match(/^(\d+(?:\.\d+)?)\s+breaths per minute$/i);
    if (match) {
      return {
        ...fallback,
        respirationMeasurement: match[1],
      };
    }
  }

  return fallback;
};
